// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Browser } from "@capacitor/browser";
import {
  WorkerRequirementDomain,
  WorkerRequirementMappingLevel,
} from "@clipboard-health/contract-worker-app-bff";
import { formatDateTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { PLACEMENT_PATHS } from "@src/appV2/redesign/Placements/paths";
import { parseISO } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { ACCOUNT_ROLES_FULL_PATH } from "../../../../../Accounts/Roles/paths";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  WORKPLACE_DOCUMENTS_FULL_PATH,
} from "../../../../../Documents/paths";
import { type PlacementDetail } from "../../../../api/useGetPlacement";
import { useRecruiterBookingFlowContext } from "../../../../hooks/useRecruiterBookingFlowContext";
import { type PrimaryAction } from "../../utils/getPrimaryAction";
import { type PlacementDerivedData } from "../usePlacementPrimaryAction";

const SIMPLE_TEXT_PHONE_NUMBER = "+19085625161";

interface UseNavigationOperationsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  primaryAction: PrimaryAction;
  derivedData: PlacementDerivedData;
}

/**
 * Handles navigation operations (profile, documents, recruiter).
 */
export function useNavigationOperations(params: UseNavigationOperationsParams) {
  const { placementDetails, placementCandidate, primaryAction, derivedData } = params;

  const history = useHistory();
  const {
    requirement,
    isProfilePending,
    highestRankingSharedQualification,
    eventContext,
    screeningCalendlyLink,
    upskillingCalendlyLink,
    showCallMeNow,
    talkToRecruiterCopy,
    workerTimezone,
    connectedPlacementId,
  } = derivedData;
  const recruiterScreeningCallFlowState = useRecruiterBookingFlowContext();

  function handleUploadCredentialAction() {
    const { missingCredentialRequirementType } = derivedData;
    if (missingCredentialRequirementType === "BASE_CREDENTIAL") {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: placementDetails.credentialRequirements?.find(
            (requirement) => requirement.attributes.level === WorkerRequirementMappingLevel.STATE
          )?.relationships.requirement.data.id,
        })
      );
    } else if (missingCredentialRequirementType === "WORKPLACE_CREDENTIAL") {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: placementDetails.credentialRequirements?.find(
            (requirement) => requirement.attributes.level === WorkerRequirementMappingLevel.HCF
          )?.relationships.requirement.data.id,
        })
      );
    }
  }

  function handleCompleteProfileAction() {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "complete-profile-clicked",
      isProfilePending,
      requirement,
      hiringEvents: placementDetails.hiringEvents?.map((event) => {
        return {
          id: event.id,
          name: event.attributes.type,
          startDate: event.attributes.fixedSchedule?.startDate,
          endDate: event.attributes.fixedSchedule?.endDate,
        };
      }),
    });
    history.push(ACCOUNT_ROLES_FULL_PATH);
  }

  function handleUploadLicenseAction() {
    history.push(ACCOUNT_ROLES_FULL_PATH);
  }

  function handleUploadDocumentAction() {
    const requiredDocuments = placementDetails.workerRequirements?.filter(
      (requirement) => !requirement.attributes.isValid
    );
    const hasSingleRequiredDocument =
      isDefined(requiredDocuments) && requiredDocuments.length === 1;

    if (hasSingleRequiredDocument) {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: requiredDocuments[0].relationships.requirement.data.id,
        })
      );
      return;
    }

    const workplaceDocumentsPath = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
      workplaceId: placementDetails.workplace.id,
    });
    const hasHighestRankingQualification = isDefined(highestRankingSharedQualification);

    const targetPath = hasHighestRankingQualification
      ? `${workplaceDocumentsPath}?qualification=${highestRankingSharedQualification}&domain=${WorkerRequirementDomain.HIRING}`
      : `${workplaceDocumentsPath}?domain=${WorkerRequirementDomain.HIRING}`;

    history.push(targetPath);
  }

  async function handleTalkToRecruiterAction() {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_TALK_TO_RECRUITER_CLICKED, {
      ...eventContext,
      placementDetails,
      placementCandidate,
      showCallMeNow,
      copyId: talkToRecruiterCopy.id,
    });

    const isUpskillingAction =
      primaryAction.label === talkToRecruiterCopy.placementAction.upskilling;

    if (isUpskillingAction) {
      logEvent(APP_V2_APP_EVENTS.PLACEMENT_COMPLETE_SKILLS_COURSE_CLICKED, {
        ...eventContext,
        placementDetails,
        placementCandidate,
        copyId: talkToRecruiterCopy.id,
      });
      await Browser.open({
        url: upskillingCalendlyLink,
        presentationStyle: "fullscreen",
      });
      return;
    }

    if (showCallMeNow) {
      recruiterScreeningCallFlowState.modalState.openModal();
      logEvent(APP_V2_APP_EVENTS.PLACEMENT_RECRUITER_SCREENING_CALL_MODAL_OPENED, {
        ...eventContext,
        placementCandidateId: placementCandidate.id,
        showCallMeNow,
        copyId: talkToRecruiterCopy.id,
        source: "detail-cta",
      });
      return;
    }

    await Browser.open({
      url: screeningCalendlyLink,
      presentationStyle: "fullscreen",
    });
  }

  function handleTalkToRecruiterNextStepsAction() {
    const workplaceName = placementDetails.workplace.name;
    const { attendedInterview } = derivedData;
    const interviewDateFormatted = attendedInterview?.attributes.start
      ? formatDateTime(parseISO(attendedInterview.attributes.start), {
          timeZone: workerTimezone,
        })
      : "";
    const interviewString = interviewDateFormatted ? ` on ${interviewDateFormatted}` : "";

    const messageBody = `Hey! I haven't heard back yet from ${workplaceName} after my interview${interviewString}. Could you help me find out what the next steps are?`;
    const encodedMessage = encodeURIComponent(messageBody);
    const smsUrl = `sms:${SIMPLE_TEXT_PHONE_NUMBER}?body=${encodedMessage}`;

    logEvent(APP_V2_APP_EVENTS.PLACEMENT_TALK_TO_RECRUITER_NEXT_STEPS_CLICKED, {
      ...eventContext,
      placementDetails,
      placementCandidate,
      workplaceName,
      interviewDate: attendedInterview?.attributes.start,
    });

    window.open(smsUrl, "_blank", "noopener,noreferrer");
  }

  function handleHasInterviewFromConnectedListingAction() {
    history.push(`${PLACEMENT_PATHS.ROOT}/${connectedPlacementId}`);
  }

  return {
    handleCompleteProfileAction,
    handleUploadLicenseAction,
    handleUploadDocumentAction,
    handleTalkToRecruiterAction,
    handleTalkToRecruiterNextStepsAction,
    handleUploadCredentialAction,
    recruiterScreeningCallFlowState,
    handleHasInterviewFromConnectedListingAction,
  };
}

export type NavigationOperationsReturn = ReturnType<typeof useNavigationOperations>;
